import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Card,
  CardContent,
  Stack,
  Drawer,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  ViewComfy as ViewComfyIcon,
  ViewCompact as ViewCompactIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { DataTableComponent } from "../components/EmailDataTableComponent/DataTableComponent";
import { buildColumnsFromConfig } from "./ShipmentsTableColumns";
import { DobbyShipmentPage } from "./DobbyShipmentPage";
import { CreateShipmentRowActions } from "./CreateShipmentRowActions";
import {
  fetchShipments,
  fetchAssignees,
  fetchTrackingSheetColumns,
  createTemporaryShipment,
  discardTemporaryShipment,
  saveTemporaryShipment,
} from "../store/shipmentSlice";
import type { AppDispatch, AppState } from "../store";
import type { ShipmentUI } from "../store/shipmentSlice";
import { useSnackbar } from "../components/SnackbarProvider";
import { useSessionRecording } from "@/components/useSessionRecording";
import { EventLogger } from "@shared/utils/eventLogger";
import type { DataTableColumn } from "../components/EmailDataTableComponent/DataTableComponent";
import { ShipmentUpdatesFeed } from "./ShipmentUpdatesFeed";
import { exportShipmentsToXlsx } from "./export/exportShipments";
import { useTableCellEditing } from "./table/editHooks";
import { ShipmentAssigneeFilters } from "./ShipmentAssigneeFilters";
import { useSortShipments } from "./hooks/useSortShipments";

interface ShipmentsPageProps {
  onOpenEmail?: (emailId: string, eventName: string) => void;
  onShipmentClick?: (shipmentId: string) => void;
}

// Custom hook for selecting activeEdit - defined outside component to have stable reference
function useActiveEditSelector() {
  const result = useSelector(
    (state: AppState) =>
      state.shipment.shipments.temporaryShipmentEdits.activeEdit,
  );
  return result;
}

export function ShipmentsPage({
  onOpenEmail,
  onShipmentClick,
}: ShipmentsPageProps) {
  const dispatch = useDispatch<AppDispatch>();
  useSessionRecording("6jyi7b/shipmentdashboard");
  const [searchParams] = useSearchParams();
  const { showSnackbar } = useSnackbar();

  // Get shipments data from Redux store (split selectors to prevent unnecessary re-renders)
  const shipmentData = useSelector(
    (state: AppState) => state.shipment.shipments.shipmentData,
  );
  const shipmentList = useSelector(
    (state: AppState) => state.shipment.shipments.shipmentList,
  );
  const loading = useSelector(
    (state: AppState) => state.shipment.shipments.loading,
  );
  const error = useSelector(
    (state: AppState) => state.shipment.shipments.error,
  );
  const hasMore = useSelector(
    (state: AppState) => state.shipment.shipments.hasMore,
  );
  const totalCount = useSelector(
    (state: AppState) => state.shipment.shipments.totalCount,
  );
  const activeCount = useSelector(
    (state: AppState) => state.shipment.shipments.activeCount,
  );

  // Get current user from Redux
  const currentUser = useSelector((state: AppState) => state.userData.user);

  // Get assignees from Redux
  const { assigneeData, assigneeList } = useSelector(
    (state: AppState) => state.shipment.assignees,
  );

  // Get tracking sheet columns from Redux
  const trackingSheetColumns = useSelector(
    (state: AppState) => state.shipment.trackingSheetColumns,
  );

  // Table cell editing handlers
  const {
    handleCellChange,
    handleCellEditStart,
    handleCellEditEnd,
    handleCellSave,
  } = useTableCellEditing(dispatch);

  // Sorting (null = no sort, use original order from API)
  // Only allow sorting for admin users
  const [sortConfig, setSortConfig] = useState<{
    columnName: string;
    direction: "asc" | "desc";
  } | null>(null);

  // Convert shipmentList to array of shipments
  const unsortedShipments = useMemo(() => {
    return shipmentList
      .map((id) => shipmentData[id])
      .filter((s): s is ShipmentUI => s !== null);
  }, [shipmentList, shipmentData]);

  // Apply sorting using the hook (only if user is admin)
  const shipments = useSortShipments(
    unsortedShipments,
    currentUser?.isAdmin ? sortConfig : null,
  );
  // Get tag from query parameter, fallback to default
  const tag = searchParams.get("tag");
  const TAGS = useMemo(() => (tag ? [tag] : []), [tag]);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState<{
    assigneeIds: string[];
    showUnassignedOnly: boolean;
  }>({ assigneeIds: [], showUnassignedOnly: false });
  const [assigneeFilterInitialized, setAssigneeFilterInitialized] =
    useState(false);

  // Density toggle
  const [density, setDensity] = useState<"comfortable" | "compact">(
    "comfortable",
  );

  // Updates feed side panel
  // const [showUpdatesFeed, setShowUpdatesFeed] = useState(false);

  // Fetch shipments using Redux action
  const handleFetchShipments = useCallback(
    (offset: number = 0, search?: string, append: boolean = false) => {
      dispatch(
        fetchShipments({
          offset,
          search,
          append,
          tags: TAGS,
          assigneeIds: assigneeFilter.assigneeIds,
          showUnassignedOnly: assigneeFilter.showUnassignedOnly,
        }),
      );
    },
    [dispatch, TAGS, assigneeFilter],
  );

  // Handle sort column click (3-state cycle: null → asc → desc → null)
  const handleSort = useCallback(
    (column: DataTableColumn<ShipmentUI>) => {
      if (!column.fieldName) return;

      const columnName = column.fieldName as string;

      // Three-state cycle
      if (!sortConfig || sortConfig.columnName !== columnName) {
        // First click or different column: sort ascending
        setSortConfig({ columnName, direction: "asc" });
      } else if (sortConfig.direction === "asc") {
        // Second click: sort descending
        setSortConfig({ columnName, direction: "desc" });
      } else {
        // Third click: clear sort, restore original order
        setSortConfig(null);
        handleFetchShipments(0, searchTerm || undefined);
      }
    },
    [sortConfig, handleFetchShipments, searchTerm],
  );

  // Filter shipments based on search term
  // const filteredShipments = shipments.filter((shipment) => {
  //   if (!searchTerm) return true;

  //   const searchLower = searchTerm.toLowerCase();

  //   // Search in shipment name
  //   if (shipment.name.toLowerCase().includes(searchLower)) return true;

  //   if (shipment.id.toLowerCase().includes(searchLower)) return true;
  //   if (shipment.name.toLowerCase().includes(searchLower)) return true;

  //   // Search in property values
  //   return shipment.properties.some((prop) =>
  //     prop.value.toLowerCase().includes(searchLower)
  //   );
  // });

  // Handle shipment selection
  const handleShipmentSelect = useCallback(
    (shipment: ShipmentUI) => {
      EventLogger.getInstance().track({
        name: "click",
        action: "open_shipment",
        shipmentId: shipment.id,
      });
      onShipmentClick?.(shipment.id);
    },
    [onShipmentClick],
  );

  // Handle search
  const handleSearch = useCallback(() => {
    EventLogger.getInstance().track({
      name: "search",
      action: "shipment_search",
      query: searchTerm,
    });
    handleFetchShipments(0, searchTerm || undefined);
  }, [handleFetchShipments, searchTerm]);

  // Handle load more
  const handleLoadMore = useCallback(() => {
    if (hasMore) {
      handleFetchShipments(shipments.length, searchTerm || undefined, true);
    }
  }, [handleFetchShipments, hasMore, shipments.length, searchTerm]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    handleFetchShipments();
  }, [handleFetchShipments]);

  // Handle export
  const handleExport = useCallback(async () => {
    try {
      await exportShipmentsToXlsx({
        tags: TAGS,
        search: searchTerm || undefined,
        format: "xlsx",
      });

      EventLogger.getInstance().track({
        name: "click",
        action: "download",
      });
    } catch (error) {
      console.error("Export failed:", error);
    }
  }, [TAGS, searchTerm]);

  // Handle create temporary shipment
  const handleCreateShipment = useCallback(() => {
    dispatch(createTemporaryShipment({ tags: TAGS }));
    EventLogger.getInstance().track({
      name: "click",
      action: "other",
    });
  }, [dispatch, TAGS]);

  // Handle save temporary shipment
  const handleSaveShipment = useCallback(
    async (tempId: string) => {
      try {
        const result = await dispatch(
          saveTemporaryShipment({ tempId }),
        ).unwrap();

        showSnackbar("Shipment created successfully", 3000, "info");
        EventLogger.getInstance().track({
          name: "click",
          action: "other",
          shipmentId: result.shipment.id,
        });
      } catch (error: any) {
        showSnackbar(`Failed to create shipment: ${error}`, 5000, "error");
      }
    },
    [dispatch, showSnackbar],
  );

  // Handle discard temporary shipment
  const handleDiscardShipment = useCallback(
    (tempId: string) => {
      dispatch(discardTemporaryShipment({ tempId }));
      EventLogger.getInstance().track({
        name: "click",
        action: "other",
      });
    },
    [dispatch],
  );

  // Row actions callback for temporary shipments
  const getRowActions = useCallback(
    (row: ShipmentUI) => {
      if (!row.isTemporary) return null;

      return (
        <CreateShipmentRowActions
          tempId={row.id}
          onSave={handleSaveShipment}
          onDiscard={handleDiscardShipment}
        />
      );
    },
    [handleSaveShipment, handleDiscardShipment],
  );

  // Row styling callback for temporary shipments
  const getRowStyling = useCallback((row: ShipmentUI) => {
    if (!row.isTemporary) return {};

    return {
      "& > td": {
        // backgroundColor: "#9c5959ff",
        borderTop: "2px solid #1976d2",
        borderBottom: "2px solid #1976d2",
      },
      "& > td:first-of-type": {
        borderLeft: "2px solid #1976d2",
      },
      "& > td:last-of-type": {
        borderRight: "2px solid #1976d2",
      },
    };
  }, []);

  // Handle assignee filter change
  const handleAssigneeFilterChange = useCallback(
    (assigneeIds: string[], showUnassignedOnly: boolean) => {
      setAssigneeFilter({ assigneeIds, showUnassignedOnly });
    },
    [],
  );

  // Initialize assignee filter with current user's assignee when assignees load
  useEffect(() => {
    if (!assigneeFilterInitialized && currentUser && assigneeList.length > 0) {
      // XX disable for now, default to "Show All"
      // Find the assignee that matches the current user
      // const currentUserAssignee = assigneeList.find((id) => {
      //   const assignee = assigneeData[id];
      //   return assignee && assignee.userId === currentUser.id && assignee.id;
      // });

      // if (currentUserAssignee) {
      //   const assignee = assigneeData[currentUserAssignee];
      //   if (assignee?.id) {
      //     setAssigneeFilter({
      //       assigneeIds: [assignee.id],
      //       showUnassignedOnly: false,
      //     });
      //   }
      // } else {
      // No assignee found for current user, default to "Show All"
      setAssigneeFilter({ assigneeIds: [], showUnassignedOnly: false });
      // }

      setAssigneeFilterInitialized(true);
    }
  }, [currentUser, assigneeList, assigneeData, assigneeFilterInitialized]);

  // Initial load
  useEffect(() => {
    dispatch(fetchAssignees({ includeOrgUsers: true }));
    dispatch(fetchTrackingSheetColumns());
  }, [dispatch]);

  // Fetch shipments when filters change (only after initialization)
  useEffect(() => {
    // Only fetch after assignee filter has been initialized
    if (assigneeFilterInitialized) {
      handleFetchShipments();
    }
  }, [handleFetchShipments, assigneeFilter, assigneeFilterInitialized]);

  return (
    <Box
      sx={{
        height: "calc(100vh - 40px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* {error && (
        <Alert severity="error" sx={{ marginBottom: "16px" }}>
          {error}
        </Alert>
      )} */}

      {/* Filters */}
      <Card className="filtersContainer" elevation={0}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            {/* <ShipmentAssigneeFilters
              selectedAssigneeIds={assigneeFilter.assigneeIds}
              showUnassignedOnly={assigneeFilter.showUnassignedOnly}
              onFilterChange={handleAssigneeFilterChange}
              currentUserId={currentUser?.id}
            /> */}
            <TextField
              size="small"
              label="Search shipments"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              sx={{ minWidth: 250 }}
              placeholder="Search by name or property values..."
            />
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              sx={{ height: 36, textTransform: "none" }}
            >
              Search
            </Button>

            <Typography variant="body2" color="text.secondary">
              Total: {totalCount} shipments, {activeCount} active
              {searchTerm && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setSearchTerm("");
                    handleFetchShipments();
                  }}
                  sx={{
                    ml: 1,
                    minWidth: "auto",
                    textTransform: "none",
                    fontSize: "12px",
                    padding: "2px 6px",
                  }}
                >
                  Clear search
                </Button>
              )}
            </Typography>
            <Box className="filler" flex="1" />

            <Tooltip title="Table density">
              <ToggleButtonGroup
                value={density}
                exclusive
                onChange={(e, newDensity) => {
                  if (newDensity !== null) {
                    setDensity(newDensity);
                  }
                }}
                size="small"
                sx={{ height: 36 }}
              >
                <ToggleButton
                  value="comfortable"
                  aria-label="comfortable density"
                >
                  <ViewComfyIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="compact" aria-label="compact density">
                  <ViewCompactIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>

            {/* <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateShipment}
              disabled={loading}
              sx={{ height: 36, textTransform: "none" }}
            >
              Create shipment
            </Button> */}

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
              sx={{ height: 36, textTransform: "none" }}
            >
              Refresh
            </Button>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={loading}
              sx={{ height: 36, textTransform: "none" }}
            >
              Export
            </Button>

            {/* <Button
              variant={showUpdatesFeed ? "contained" : "outlined"}
              startIcon={<TimelineIcon />}
              onClick={() => setShowUpdatesFeed(!showUpdatesFeed)}
              sx={{ height: 36, textTransform: "none" }}
            >
              Updates feed
            </Button> */}
          </Stack>
        </CardContent>
      </Card>

      {/* Shipments Table */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <DataTableComponent
          isLoading={loading}
          // rows={searchTerm ? filteredShipments : shipments}
          rows={shipments}
          columns={buildColumnsFromConfig(trackingSheetColumns)}
          keyField="id"
          onRowClick={handleShipmentSelect}
          onSort={currentUser?.isAdmin ? handleSort : undefined}
          sortColumn={
            currentUser?.isAdmin ? (sortConfig?.columnName as any) : null
          }
          sortDirection={
            currentUser?.isAdmin ? sortConfig?.direction : undefined
          }
          stickyHeader={true}
          showColumnVisibilityToggle={true}
          onEndReached={!searchTerm ? handleLoadMore : undefined}
          // editable={true}
          onCellSave={handleCellSave}
          onCellChange={handleCellChange}
          onCellEditStart={handleCellEditStart}
          onCellEditEnd={handleCellEditEnd}
          useActiveEditHook={useActiveEditSelector}
          rowActions={getRowActions}
          getRowSx={getRowStyling}
          fixedWidth={2800}
          density={density}
          afterLastRow={
            !searchTerm && hasMore ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <Button onClick={handleLoadMore} disabled={loading}>
                  Load more
                </Button>
              </Box>
            ) : null
          }
          sx={{ height: "100%" }}
        />
      </Box>

      {/* Updates Feed Side Panel */}
      {/* <Drawer
        anchor="right"
        open={showUpdatesFeed}
        onClose={() => setShowUpdatesFeed(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: 400,
            boxSizing: "border-box",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="h6">Updates Feed</Typography>
          <IconButton onClick={() => setShowUpdatesFeed(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <ShipmentUpdatesFeed tags={TAGS} onShipmentClick={onShipmentClick} />
      </Drawer> */}
    </Box>
  );
}

// Main component that uses the wrapper
export function ShipmentsPageWrapper() {
  return (
    <DobbyShipmentPage title="Shipments">
      <ShipmentsPage />
    </DobbyShipmentPage>
  );
}
